// The serving engine: route a request, fetch + re-verify the resource, apply mode headers, fail
// closed on any integrity failure (szzt-spec.md §9.4, §9.6, §9.9).
//
// This is the reusable core both the verifier service worker (sw.ts) and a server gateway call. It
// is transport-agnostic: bytes come through the VerifyingCache's Fetcher seam, and every resource is
// re-verified on read (§9.4). A verification failure returns a defined, non-blank, debuggable
// surface — a distinct 502 status and a body naming the path and expected-vs-computed CID — rather
// than a silent or blank error.

import { siteHeaders, tileHeaders } from "./headers.ts";
import { route } from "./routing.ts";
import { UnsafeHeaderError, VerificationError } from "./types.ts";
import type { SzztIndex, ServeDecision, ServeMode, ServeRequest } from "./types.ts";
import type { VerifyingCache } from "./verify-cache.ts";

const DIAGNOSTIC_CT = "text/plain; charset=utf-8";

/** Headers on error/404 diagnostics: never cache a fail-closed or not-found response. */
function diagnosticHeaders(extra?: Record<string, string>): Record<string, string> {
  return { "content-type": DIAGNOSTIC_CT, "cache-control": "no-store", ...extra };
}

/**
 * Derive the navigation flag from request headers (§9.9): a `Sec-Fetch-Mode: navigate` request, or —
 * when that header is absent — an `Accept` that includes `text/html`. Gates SPA fallback.
 */
export function isNavigation(headers: {
  secFetchMode?: string | null;
  accept?: string | null;
}): boolean {
  if (headers.secFetchMode) return headers.secFetchMode === "navigate";
  return (headers.accept ?? "").includes("text/html");
}

/**
 * Serve a request against a signed index in the given mode. Returns a {@link ServeDecision}
 * (status + headers + body); never throws — integrity failures become fail-closed 502 diagnostics.
 */
export async function serve(
  index: SzztIndex,
  cache: VerifyingCache,
  req: ServeRequest,
  mode: ServeMode = "site",
): Promise<ServeDecision> {
  const routed = route(index, req);

  if (routed.kind === "redirect") {
    // Routing 3xx (§9.9): Location only, no body.
    return { status: routed.status, headers: { location: routed.location }, body: null };
  }

  const loc = routed.location;
  if (loc === undefined) {
    // Default 404 — no custom notFound resource configured.
    return { status: 404, headers: diagnosticHeaders(), body: `404 Not Found: ${req.path}\n` };
  }

  try {
    const bytes = await cache.getBlob({ did: index.did, cid: loc.cid, path: loc.path });
    const headers = mode === "tile" ? tileHeaders(loc) : siteHeaders(loc);
    return { status: routed.status, headers, body: bytes };
  } catch (err) {
    if (err instanceof VerificationError) {
      // Fail closed with a debuggable surface (§9.4).
      return {
        status: 502,
        headers: diagnosticHeaders({ "x-szzt-verify": "failed" }),
        body:
          `502 Verification Failed\n` +
          `path: ${err.path}\n` +
          `expected CID: ${err.expectedCid}\n` +
          `computed CID: ${err.actualCid}\n`,
      };
    }
    if (err instanceof UnsafeHeaderError) {
      // Fail closed (roadmap H2): a hostile content-type/CSP would crash res.writeHead() — refuse this
      // ONE resource with a debuggable diagnostic instead, never the process.
      return {
        status: 502,
        headers: diagnosticHeaders({ "x-szzt-verify": "failed" }),
        body: `502 Unsafe Header Value\npath: ${loc.path}\nfield: ${err.field}\n`,
      };
    }
    // Upstream fetch failure (blob unavailable) — also fail closed, distinct surface.
    const message = err instanceof Error ? err.message : String(err);
    return {
      status: 502,
      headers: diagnosticHeaders({ "x-szzt-verify": "unavailable" }),
      body: `502 Upstream Fetch Failed\npath: ${loc.path}\ncid: ${loc.cid}\n${message}\n`,
    };
  }
}
